// CTW-DURMAH-STUDENT-PAINPOINTS-SEO-RESOURCE-EXPANSION-03:
// Independent learning cluster article page: preparing for law seminars
// and tutorials. Fully public. Content lives in the data module.

import React from "react";
import { RelatedVideoLessons } from "@/components/resources/RelatedVideoLessons";
import Head from "next/head";
import {
  preparingForLawSeminarsSections,
  preparingForLawSeminarsSources,
} from "@/content/resources/preparingForLawSeminars";
import type { ResourceSection } from "@/content/resources/types";
import {
  ResourceBreadcrumbs,
  ResourceCrossLinks,
  ResourceDisclaimer,
  ResourceHero,
  ResourceSoftCta,
  SectionSourceLinks,
  SourcesSection,
} from "@/components/resources/ResourcePrimitives";

const SITE = "https://www.durmah.ai";

function SectionBlock({ section }: { section: ResourceSection }) {
  return (
    <section id={section.id} className="mb-14 scroll-mt-32">
      <h2 className="mb-4 text-2xl md:text-3xl font-black text-gray-900 dark:text-white">
        {section.heading}
      </h2>
      {section.paragraphs.map((p, i) => (
        <p key={i} className="mb-4 leading-relaxed text-gray-600 dark:text-slate-400">
          {p}
        </p>
      ))}
      {section.bullets && (
        <ul className="mb-4 space-y-3">
          {section.bullets.map((b, i) => (
            <li key={i} className="flex gap-3 leading-relaxed text-gray-600 dark:text-slate-400">
              <span aria-hidden className="mt-[10px] h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
      {section.callout && (
        <div className="mt-5 rounded-2xl border border-indigo-100 dark:border-indigo-500/20 bg-indigo-50/70 dark:bg-indigo-500/5 p-6">
          <p className="mb-1.5 text-sm font-black text-indigo-900 dark:text-indigo-300">
            {section.callout.title}
          </p>
          <p className="m-0 text-sm leading-relaxed text-indigo-900/80 dark:text-indigo-200/70">
            {section.callout.body}
          </p>
        </div>
      )}
      <SectionSourceLinks links={section.links} />
    </section>
  );
}

export default function PreparingForLawSeminarsPage() {
  const canonical = `${SITE}/resources/preparing-for-law-seminars`;
  const title = "How to Prepare for Law Seminars and Tutorials | Durmah";
  const description =
    "What prepared actually means for a law seminar: reading the questions first, a realistic preparation sequence, speaking up when everyone seems smarter, rescuing the week you did not prepare, and the ten minutes after the seminar that most students waste.";

  const articleSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "How to Prepare for Law Seminars and Tutorials",
    description,
    url: canonical,
    isAccessibleForFree: true,
    author: { "@type": "Organization", name: "Durmah" },
    publisher: {
      "@type": "Organization",
      name: "Durmah",
      logo: { "@type": "ImageObject", url: `${SITE}/android-chrome-512x512.png` },
    },
    datePublished: "2026-07-10",
    dateModified: "2026-07-10",
  });

  const breadcrumbSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Student Resources", item: `${SITE}/resources` },
      { "@type": "ListItem", position: 2, name: "Preparing for Law Seminars", item: canonical },
    ],
  });

  return (
    <div className="bg-white dark:bg-[#020617] min-h-screen flex flex-col pt-32">
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link key="route-canonical" rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta key="route-og-url" property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: articleSchema }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbSchema }} />
      </Head>

      <main className="flex-1 py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ResourceBreadcrumbs current="Preparing for Law Seminars" />
          <ResourceHero
            eyebrow="Independent learning"
            title={
              <>
                Preparing for{" "}
                <span className="text-indigo-600 dark:text-indigo-400">Law Seminars</span>
              </>
            }
            subtitle="A lecture lets you hide; a seminar does not. Almost every law student knows the dread of being called on for a case they only skimmed. This guide covers what prepared actually means, a preparation sequence that fits a real week, how to speak up when everyone seems smarter, and what to do in the week the preparation did not happen."
          />

          {/* In-page navigation */}
          <nav aria-label="On this page" className="mb-12 rounded-2xl border border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-900/40 p-6">
            <p className="mb-3 text-xs font-black uppercase tracking-widest text-gray-500 dark:text-slate-500">
              On this page
            </p>
            <ul className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
              {preparingForLawSeminarsSections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-sm font-medium text-indigo-700 dark:text-indigo-400 hover:underline"
                  >
                    {s.heading}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {preparingForLawSeminarsSections.map((section) => (
            <SectionBlock key={section.id} section={section} />
          ))}

          <RelatedVideoLessons
            heading="Recover a rushed seminar preparation"
            intro="Use this short triage method when time is tight, then return to the full preparation required by your module."
            slugs={["prepare-for-a-law-seminar-in-fifteen-minutes", "half-page-case-brief", "precise-law-seminar-question"]}
          />
          <ResourceCrossLinks
            items={[
              {
                title: "How to Read a Case",
                description:
                  "The layered reading and half-page briefing method your seminar preparation runs on.",
                href: "/resources/how-to-read-a-case",
              },
              {
                title: "Making Sense of Legal Language",
                description:
                  "The vocabulary side of seminar confidence: decoding terms and saying them out loud.",
                href: "/resources/making-sense-of-legal-language",
              },
              {
                title: "Second Year: What Changes",
                description:
                  "Why seminar craft matters more as the modules get denser and the marks start counting.",
                href: "/resources/second-year-law-what-changes",
              },
            ]}
          />

          <SourcesSection groups={preparingForLawSeminarsSources} />
          <ResourceSoftCta
            title="Want to rehearse before the room?"
            body="Durmah is a practice partner for seminar preparation: test yourself on the doctrines, talk through your position out loud, and check your grasp of the cases you briefed. The reading and the opinions stay yours. This guide stays free either way."
          />
          <ResourceDisclaimer />
        </div>
      </main>
    </div>
  );
}
